import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Bell, FileWarning, Clock, ScanLine, FileText, ClipboardList, Banknote, Loader2, RefreshCw,
} from "lucide-react";
import api from "../../lib/api";

interface NotificationItem {
  id: string;
  kind: string;
  severity: "danger" | "warning" | "info";
  title: string;
  description: string;
  link: string;
  count: number;
}

const KIND_ICONS: Record<string, typeof Bell> = {
  invoices_overdue: FileWarning,
  quotes_expired: Clock,
  quotes_expiring: Clock,
  ocr_failed: ScanLine,
  draft_invoices: FileText,
  draft_quotes: ClipboardList,
  payments_unlinked: Banknote,
};

const SEVERITY_STYLES: Record<NotificationItem["severity"], { dot: string; icon: string }> = {
  danger: { dot: "bg-red-500", icon: "bg-red-50 text-red-600" },
  warning: { dot: "bg-amber-500", icon: "bg-amber-50 text-amber-600" },
  info: { dot: "bg-blue-500", icon: "bg-blue-50 text-blue-600" },
};

const PANEL_W = 380;

interface NotificationsPanelProps {
  /** Totaal aantal open aandachtspunten — ook gebruikt voor de badge op de bel. */
  count: number;
  open: boolean;
  onClose: () => void;
  onOpened: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}

/** Notificatie-paneel (bel-icoontje in de Topbar). Portaal onder de bel-knop,
 *  sluit bij klik-buiten of Escape. Items zijn afgeleide aandachtspunten uit
 *  GET /api/notifications; klik → naar de bijbehorende pagina. */
export function NotificationsPanel({ count, open, onClose, onOpened, anchorRef }: NotificationsPanelProps) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<NotificationItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 64, left: 0 });

  // Positioneer het paneel onder de bel-knop bij openen.
  useEffect(() => {
    if (!open) return;
    const r = anchorRef.current?.getBoundingClientRect();
    if (r) {
      setCoords({
        top: r.bottom + 8,
        left: Math.max(8, Math.min(r.right - PANEL_W, window.innerWidth - PANEL_W - 8)),
      });
    }
  }, [open, anchorRef]);

  // Klik-buiten + Escape sluiten.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  // Laad de notificaties bij elke opening.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    api
      .get("/api/notifications")
      .then(({ data }) => {
        if (alive) setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (alive) setItems([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      style={{ position: "fixed", top: `${coords.top}px`, left: `${coords.left}px`, width: `${PANEL_W}px`, zIndex: 50 }}
      className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">
        <div className="flex items-center gap-2">
          <Bell size={15} className="text-slate-500" />
          <p className="text-sm font-bold text-slate-800">Meldingen</p>
          {count > 0 && (
            <span className="rounded-full bg-red-100 text-red-600 text-[10px] font-bold px-1.5 py-0.5">
              {count}
            </span>
          )}
        </div>
        <button
          onClick={onOpened}
          title="Verversen"
          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {loading && items === null && (
          <p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-slate-400">
            <Loader2 size={15} className="animate-spin" /> Laden…
          </p>
        )}
        {!loading && items && items.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-slate-400">
            Alles up-to-date — geen open aandachtspunten ✅
          </p>
        )}
        {items && items.length > 0 && (
          <div className="py-1">
            {items.map((n) => {
              const Icon = KIND_ICONS[n.kind] || Bell;
              const sev = SEVERITY_STYLES[n.severity];
              return (
                <button
                  key={n.id}
                  onClick={() => {
                    onClose();
                    navigate(n.link);
                  }}
                  className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${sev.icon}`}>
                    <Icon size={15} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">{n.title}</span>
                      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${sev.dot}`} title={n.severity} />
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5">{n.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}